import { useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { authService } from "@/services/authService";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { formatError } from "@/services/api";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { Lock, Eye, EyeOff } from "lucide-react";

export default function ChangePasswordPage() {
  const { user, updateUser, logout } = useAuth();
  const navigate = useNavigate();
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (newPassword.length < 6) {
      toast.error("New password must be at least 6 characters");
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }
    setLoading(true);
    try {
      const { data } = await authService.changePassword(currentPassword, newPassword);
      updateUser(data.user);
      toast.success("Password changed successfully!");
      navigate("/dashboard");
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex h-screen" data-testid="change-password-page">
      <div className="flex-1 flex items-center justify-center p-8 bg-white">
        <div className="w-full max-w-sm space-y-8">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 rounded-lg bg-[#003DA5]/10 flex items-center justify-center">
                <Lock className="w-5 h-5 text-[#003DA5]" />
              </div>
              <h1 className="font-heading text-3xl font-black tracking-tight text-[#09090B]" data-testid="change-password-title">
                Set Your Password
              </h1>
            </div>
            <p className="text-sm text-[#52525B] mt-3">
              Welcome, <span className="font-semibold text-[#09090B]">{user?.name}</span>. 
              For security, please set a new password before continuing.
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5" data-testid="change-password-form">
            <div className="space-y-2">
              <Label htmlFor="currentPassword" className="text-xs tracking-[0.15em] uppercase font-bold text-[#52525B]">
                Current Password
              </Label>
              <div className="relative">
                <Input
                  id="currentPassword"
                  type={showCurrent ? "text" : "password"}
                  data-testid="current-password-input"
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  placeholder="Enter current password"
                  className="rounded-md border-zinc-300 h-10 focus:border-[#003DA5] focus:ring-1 focus:ring-[#003DA5] pr-10"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowCurrent(!showCurrent)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-600"
                  tabIndex={-1}
                >
                  {showCurrent ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="newPassword" className="text-xs tracking-[0.15em] uppercase font-bold text-[#52525B]">
                New Password
              </Label>
              <div className="relative">
                <Input
                  id="newPassword"
                  type={showNew ? "text" : "password"}
                  data-testid="new-password-input"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="At least 6 characters"
                  className="rounded-md border-zinc-300 h-10 focus:border-[#003DA5] focus:ring-1 focus:ring-[#003DA5] pr-10"
                  required
                  minLength={6}
                />
                <button
                  type="button"
                  onClick={() => setShowNew(!showNew)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-600"
                  tabIndex={-1}
                >
                  {showNew ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="confirmPassword" className="text-xs tracking-[0.15em] uppercase font-bold text-[#52525B]">
                Confirm New Password
              </Label>
              <Input
                id="confirmPassword"
                type="password"
                data-testid="confirm-password-input"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Re-enter new password"
                className="rounded-md border-zinc-300 h-10 focus:border-[#003DA5] focus:ring-1 focus:ring-[#003DA5]"
                required
              />
            </div>
            <Button
              type="submit"
              data-testid="change-password-submit-button"
              className="w-full rounded-md bg-[#003DA5] hover:bg-[#002D7A] text-white font-semibold h-11 text-sm"
              disabled={loading}
            >
              {loading ? "Updating..." : "Set New Password"}
            </Button>
          </form>

          <button
            onClick={logout}
            data-testid="change-password-logout-btn"
            className="w-full text-center text-xs text-[#52525B] hover:text-[#09090B] transition-colors"
          >
            Sign out and return to login
          </button>
        </div>
      </div>

      <div className="hidden lg:block lg:flex-1 relative overflow-hidden">
        <img
          src="https://images.unsplash.com/photo-1591075246923-76a081d21e25?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzV8MHwxfHNlYXJjaHw0fHxoaWdoJTIwdm9sdGFnZSUyMHRyYW5zbWlzc2lvbiUyMHRvd2VyJTIwbGF0dGljZSUyMHN0ZWVsJTIwcHlsb258ZW58MHx8fHwxNzc2MDUxODMyfDA&ixlib=rb-4.1.0&q=85"
          alt="High voltage transmission tower"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#003DA5]/60 via-transparent to-transparent" />
        <div className="absolute bottom-12 left-12 right-12">
          <div className="bg-white/90 backdrop-blur-sm p-6 rounded-sm border border-white/20">
            <p className="font-heading text-lg font-bold text-[#09090B]">Security First</p>
            <p className="text-sm text-[#52525B] mt-1">Setting a personal password ensures that only you have access to your account.</p>
          </div>
        </div>
      </div>
    </div>
  );
}
